// Single canonical identifier-detector. Lifted from the API service so the
// frontend can reuse it for the auto-detect register / login UX without
// drifting from server-side dispatch.
//
// Detection order (deliberate; never falls back across types):
//   1. contains `@`      → email (lower-cased, trimmed)
//   2. starts with `+` OR digits (after stripping spaces / `-` / `()`) → phone
//   3. matches USERNAME_RE → username
//   4. otherwise          → null
const PHONE_BODY_RE = /^\+?\d{7,15}$/;
const CHINA_MAINLAND_MOBILE_RE = /^1[3-9]\d{9}$/;
const CHINA_MAINLAND_MOBILE_WITH_CC_RE = /^86(1[3-9]\d{9})$/;
const USERNAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9._-]{2,31}$/;
export function compactPhone(raw) {
    return raw.trim().replace(/[\s\-().]/g, '');
}
export function normalizePhoneIdentifier(raw) {
    const compacted = compactPhone(raw);
    if (!PHONE_BODY_RE.test(compacted))
        return null;
    const digits = compacted.startsWith('+') ? compacted.slice(1) : compacted;
    if (CHINA_MAINLAND_MOBILE_RE.test(digits))
        return `+86${digits}`;
    if (CHINA_MAINLAND_MOBILE_WITH_CC_RE.test(digits))
        return `+${digits}`;
    return compacted;
}
export function phoneLookupCandidates(raw) {
    const compacted = compactPhone(raw);
    const normalized = normalizePhoneIdentifier(raw);
    const candidates = new Set();
    if (PHONE_BODY_RE.test(compacted))
        candidates.add(compacted);
    if (normalized !== null)
        candidates.add(normalized);
    const digits = compacted.startsWith('+') ? compacted.slice(1) : compacted;
    if (PHONE_BODY_RE.test(compacted)) {
        if (compacted.startsWith('+')) {
            candidates.add(digits);
        }
        else {
            candidates.add(`+${compacted}`);
        }
    }
    const chinaWithCc = CHINA_MAINLAND_MOBILE_WITH_CC_RE.exec(digits);
    if (chinaWithCc !== null) {
        candidates.add(chinaWithCc[1] ?? '');
        candidates.add(`+${digits}`);
    }
    else if (CHINA_MAINLAND_MOBILE_RE.test(digits)) {
        candidates.add(`86${digits}`);
        candidates.add(`+86${digits}`);
    }
    candidates.delete('');
    return [...candidates];
}
export function parseIdentifier(raw) {
    const trimmed = raw.trim();
    if (trimmed.length === 0)
        return null;
    if (trimmed.includes('@')) {
        return { kind: 'email', value: trimmed.toLowerCase() };
    }
    const phoneCandidate = normalizePhoneIdentifier(trimmed);
    if (phoneCandidate !== null) {
        return { kind: 'phone', value: phoneCandidate };
    }
    if (USERNAME_RE.test(trimmed)) {
        return { kind: 'username', value: trimmed };
    }
    return null;
}
/** Light-weight detector for live UI hints. Returns the *intended* kind
 *  even when the value is incomplete — `me@` returns 'email' so the
 *  leading icon flips immediately to the mail glyph as the user types.
 *  `parseIdentifier` is the strict version used at submit time. */
export function inferIdentifierKind(raw) {
    const trimmed = raw.trim();
    if (trimmed.length === 0)
        return null;
    if (trimmed.includes('@'))
        return 'email';
    // A leading `+` or a stripped-digits-only body of length >= 4 is a strong
    // phone signal even if the regex isn't satisfied yet.
    const stripped = compactPhone(trimmed);
    if (stripped.startsWith('+') || (/^\d+$/.test(stripped) && stripped.length >= 4)) {
        return 'phone';
    }
    // Anything else looks like a username candidate.
    if (USERNAME_RE.test(trimmed))
        return 'username';
    return null;
}
/** Extract the local part of an email (before the `@`). Returns null when
 *  the input isn't a plausible email. Used by the password-policy PII check. */
export function emailLocalPart(email) {
    const at = email.indexOf('@');
    if (at <= 0)
        return null;
    return email.slice(0, at);
}
//# sourceMappingURL=identifiers.js.map